import React from 'react';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import { FieldCard } from './ui/FieldCard';

function intOr0(v) {
  if (v === '' || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0;
}

function clampIfMax(n, max) {
  if (max == null) return Math.max(0, n);
  if (!Number.isFinite(max) || max <= 0) return Math.max(0, n);
  return Math.max(0, Math.min(max, n));
}

function normName(s) {
  return String(s || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

// ✅ TEK rowKey standardı
function rowKeyOf(activityId, activityName, idx) {
  if (activityId != null && String(activityId) !== '') return `id:${activityId}`;
  const n = normName(activityName);
  return n ? `name:${n}` : `idx:${idx}`;
}

const MiniField = React.memo(function MiniField({ title, tip, value, onChange, max }) {
  const hasMax = Number.isFinite(max) && max > 0;
  const [draft, setDraft] = React.useState(value == null ? '' : String(value));

  React.useEffect(() => {
    const next = value == null ? '' : String(value);
    setDraft(next);
  }, [value]);

  const commit = React.useCallback(() => {
    if (draft === '') {
      // boşsa null gönderebilirsin, ama istersen 0 yap:
      onChange?.(0);
      return;
    }
    if (!/^\d+$/.test(draft)) return;

    const n = intOr0(draft);
    onChange?.(clampIfMax(n, max));
  }, [draft, onChange, max]);

  return (
    <MDBox sx={{ width: { xs: 78, sm: 84, md: 88 } }} onMouseDown={(e) => e.stopPropagation()}>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={0.55}
        sx={{ mb: 0.45 }}
      >
        <MDTypography
          variant="caption"
          sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1 }}
        >
          {title}
        </MDTypography>
        <Tooltip title={tip}>
          <Icon sx={{ fontSize: 14, opacity: 0.7, cursor: 'help' }}>info</Icon>
        </Tooltip>
      </MDBox>

      <MDInput
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            setDraft('');
            return;
          }
          if (!/^\d+$/.test(raw)) return;
          setDraft(raw);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        inputProps={hasMax ? { 'aria-label': `${title} (max ${max})` } : { 'aria-label': title }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '999px',
            height: { xs: 34, sm: 36 },
            background: 'rgba(255,255,255,.75)',
            backdropFilter: 'blur(6px)',
            '& fieldset': { borderColor: 'rgba(0,0,0,.12)' },
            '&:hover fieldset': { borderColor: 'rgba(0,0,0,.22)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(76,175,80,.55)' },
          },
          '& input': {
            textAlign: 'center',
            padding: '6px 10px',
            fontSize: { xs: 12.5, sm: 13 },
            fontWeight: 800,
            color: 'text.primary',
          },
        }}
      />

      {hasMax ? (
        <MDTypography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 0.25,
            fontSize: 10.5,
            color: 'text.primary',
            opacity: 0.55,
            fontWeight: 700,
          }}
        >
          / {max}
        </MDTypography>
      ) : null}
    </MDBox>
  );
});

export function MembershipUsageCard({ planSnapshot, usageByItem, onChangeUsage }) {
  const items = React.useMemo(
    () => (Array.isArray(planSnapshot?.items) ? planSnapshot.items : []),
    [planSnapshot]
  );

  // ✅ usageMap key’leri rowKey standardında
  const usageMap = React.useMemo(() => {
    const m = new Map();
    (usageByItem || []).forEach((u, idx) => {
      const key = rowKeyOf(u?.activityId, u?.activityName, idx);
      if (key) m.set(key, u);
    });
    return m;
  }, [usageByItem]);

  const setRow = React.useCallback(
    (rowKey, patch, it, idx) => {
      const next = [...(usageByItem || [])];

      const foundIdx = next.findIndex(
        (x, j) => rowKeyOf(x?.activityId, x?.activityName, j) === rowKey
      );

      const base =
        foundIdx >= 0
          ? next[foundIdx]
          : {
              activityId: it?.activityId ?? null,
              activityName: String(it?.activityName || '').trim(),
              usedSessions: 0,
              usedMakeUp: 0,
              usedGift: 0,
            };

      const merged = { ...base, ...patch };

      // ✅ activityName boş kalmasın
      merged.activityName = String(merged.activityName || '').trim();

      if (foundIdx >= 0) next[foundIdx] = merged;
      else next.push(merged);

      onChangeUsage?.(next);
    },
    [usageByItem, onChangeUsage]
  );

  const rows = React.useMemo(() => {
    return items.map((it, idx) => {
      const key = rowKeyOf(it?.activityId, it?.activityName, idx);
      const cur = usageMap.get(key) || {
        activityId: it?.activityId ?? null,
        activityName: String(it?.activityName || '').trim(),
        usedSessions: 0,
        usedMakeUp: 0,
        usedGift: 0,
      };
      return { it, cur, key, idx };
    });
  }, [items, usageMap]);

  if (!planSnapshot || items.length === 0) return null;

  return (
    <FieldCard
      icon="history"
      tone="amber"
      title="Kullanım"
      subtitle="Üye paketi daha önce kullanmaya başladıysa, kullanılan hakları gir"
    >
      <MDBox sx={{ display: 'grid', gap: { xs: 0.9, md: 1.1 } }}>
        {rows.map(({ it, cur, key, idx }) => {
          const maxSessions = Number(it.sessions);
          const maxMakeUp = Number(it.makeUp);

          return (
            <MDBox
              key={key}
              sx={{
                p: { xs: 1, sm: 1.1 },
                borderRadius: '18px',
                border: { xs: '1px solid rgba(76,175,80,.28)', md: '1px solid rgba(0,0,0,.08)' },
                background: {
                  xs: 'linear-gradient(180deg, rgba(76,175,80,.10), rgba(76,175,80,.03))',
                  md: 'linear-gradient(180deg, rgba(0,0,0,.02), rgba(0,0,0,.00))',
                },
                boxShadow: { xs: '0 10px 26px rgba(0,0,0,.06)', md: '0 6px 16px rgba(0,0,0,.05)' },
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                gap: { xs: 1, md: 1.2 },
                alignItems: 'center',
              }}
            >
              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography
                  sx={{
                    fontWeight: 900,
                    color: 'text.primary',
                    lineHeight: 1.15,
                    fontSize: { xs: '0.98rem', md: '1.02rem' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {it.activityName ||
                    (it.activityId != null ? `Faaliyet #${it.activityId}` : 'Faaliyet')}
                </MDTypography>

                <MDTypography
                  variant="caption"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.8,
                    mt: 0.45,
                    color: 'text.primary',
                    opacity: 0.7,
                    fontWeight: 700,
                  }}
                >
                  Hak: {Number.isFinite(maxSessions) && maxSessions > 0 ? maxSessions : '—'} seans •{' '}
                  {Number.isFinite(maxMakeUp) && maxMakeUp > 0 ? maxMakeUp : 0} telafi
                </MDTypography>
              </MDBox>

              <MDBox
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', md: 'auto auto auto' },
                  gap: { xs: 0.75, md: 1 },
                  justifyContent: { md: 'end' },
                }}
              >
                <MiniField
                  title="Seans"
                  tip="Normal seanslardan kaç tanesi kullanıldı?"
                  value={cur.usedSessions}
                  max={maxSessions}
                  onChange={(v) => setRow(key, { usedSessions: v }, it, idx)}
                />
                <MiniField
                  title="Telafi"
                  tip="Telafi seanslarından kaç tanesi kullanıldı?"
                  value={cur.usedMakeUp}
                  max={maxMakeUp}
                  onChange={(v) => setRow(key, { usedMakeUp: v }, it, idx)}
                />
                <MiniField
                  title="Hediye"
                  tip="Hediye seanslardan kaç tanesi kullanıldı?"
                  value={cur.usedGift}
                  max={null}
                  onChange={(v) => setRow(key, { usedGift: v }, it, idx)}
                />
              </MDBox>
            </MDBox>
          );
        })}
      </MDBox>
    </FieldCard>
  );
}
